import { useChat } from '@ai-sdk/react';
import { TextStreamChatTransport } from 'ai';
import { useState, useEffect, useMemo } from 'react';

// import { defaultSuggestionsValue } from '../../(main)/store/components/chatbot/chatbot';
import { updateThreadSession, getThreadMessagesKey } from './chat-utils';
import type { User } from './types';
import { useGetSuggestQuestions } from '@/api/use-get-suggest-questions';

interface UseChatLogicProps {
  threadId: string;
  user?: User;
  cityId?: string;
  address?: {
    lat?: number;
    lng?: number;
  };
}

export function useChatLogic({
  threadId,
  user,
  cityId,
  address,
}: UseChatLogicProps) {
  const [status, setStatus] = useState<
    'submitted' | 'streaming' | 'ready' | 'error'
  >('ready');

  // Sử dụng useGetSuggestQuestions để lấy suggestions từ API
  const { data: suggestQuestionsData, refetch: refetchSuggestions } =
    useGetSuggestQuestions(
      user?.cunstomerId && cityId
        ? {
            customer_id: user.cunstomerId,
            city_id: cityId,
          }
        : undefined
    );

  const { messages, sendMessage, setMessages } = useChat({
    transport: new TextStreamChatTransport({
      api: process.env.NEXT_PUBLIC_RECOMMENDATION_API,
      prepareSendMessagesRequest: ({ id, messages }) => {
        const lastMessage = messages[messages.length - 1];
        return {
          body: {
            question:
              lastMessage?.parts[0] && lastMessage.parts[0].type === 'text'
                ? (lastMessage.parts[0] as { type: 'text'; text: string }).text
                : '',
            thread_id: threadId ?? id,
            customer_id: user?.cunstomerId,
            city_id: cityId,
            lat: address?.lat,
            lng: address?.lng,
            limit: 20,
          },
        };
      },
    }),
    onData: () => {
      setStatus('ready');
      updateThreadSession(threadId, user?.id ?? '', Date.now());
    },
    onFinish: () => {
      setStatus('ready');
      updateThreadSession(threadId, user?.id ?? '', Date.now());
    },
    onError: () => {
      setStatus('error');
    },
  });

  const defaultSuggestions = useMemo(() => {
    if (suggestQuestionsData?.suggest_question?.length) {
      return suggestQuestionsData.suggest_question;
    }
    // return defaultSuggestionsValue;
  }, [suggestQuestionsData]);

  // Persist messages to localStorage when they change and threadId exists
  useEffect(() => {
    if (threadId && messages.length > 0) {
      try {
        localStorage.setItem(
          getThreadMessagesKey(threadId),
          JSON.stringify(messages)
        );
      } catch {
        /* empty */
      }
    }
  }, [messages, threadId]);

  // Restore messages from localStorage when threadId changes
  useEffect(() => {
    if (threadId && setMessages) {
      const key = getThreadMessagesKey(threadId);
      const raw = localStorage.getItem(key);
      if (raw) {
        try {
          const parsed = JSON.parse(raw);
          if (Array.isArray(parsed) && parsed.length > 0) {
            setMessages(parsed);
          }
        } catch {
          /* empty */
        }
      }
    }
  }, [threadId, setMessages]);

  useEffect(() => {
    if (threadId && cityId) {
      void refetchSuggestions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threadId, cityId]);

  return {
    messages,
    sendMessage,
    status,
    setStatus,
    defaultSuggestions,
  };
}
